import { IPoint, IShape } from 'canvas/types'
import Konva from 'konva'
import { takeoffStore } from 'store/store'
import { lighten } from 'utils/color'
import { BaseShape } from './abstract-shape'
import { Anchor } from './anchor'
import { hasAccess } from 'utils/features'
import { TakeoffFeature } from 'api/dto/feature'

export class Area extends BaseShape<Konva.Line> implements IShape {
  public anchorGroup: Konva.Group
  public anchors: Anchor[] = []

  #unsubscribe: () => void

  constructor(shape: IShape) {
    const storeState = takeoffStore.getState()
    const currentPoints = shape.points.map((point) => [point.x, point.y]).reduce((a, b) => [...a].concat(b), [])
    const instance = new Konva.Line({
      closed: true,
      points: currentPoints,
      fill: !storeState.isDrawing ? lighten(shape.color || '#cecece') : undefined,
      stroke: shape.color,
      strokeWidth: shape.stroke?.width || 5,
    })

    super(shape, instance)

    this.anchorGroup = new Konva.Group()

    instance.perfectDrawEnabled(false)

    this.instance.on('click', this.onMouseClick)
    this.instance.on('tap', this.onMouseClick)
    this.instance.on('dragend', this.onMouseMoveEnd)

    this.points.forEach((point) => {
      if (hasAccess(TakeoffFeature.TOOLBAR)) {
        this.#addAnchor(point)
      }
    })

    this.#unsubscribe = takeoffStore.subscribe((state) => {
      if (this.isSelected(state.selectedIds)) {
        this.instance.stroke('red')
      } else {
        this.instance.stroke(this.color)
      }
    })
  }

  remove(): void {
    this.instance.remove()
    this.instance.destroy()

    this.anchors.forEach((s) => s.remove())
    this.anchorGroup.remove()
    this.anchorGroup.destroy()

    this.#unsubscribe()
  }

  addPoint(point: IPoint, index?: number) {
    const layer = this.instance.getLayer()
    if (layer == null) {
      return
    }

    const points = [...this.points]

    if (index !== undefined) {
      points.splice(index, 0, point)
    } else {
      points.push(point)
    }

    this.instance.points(points.map((point) => [point.x, point.y]).reduce((a, b) => [...a].concat(b)))

    this.points = points

    this.#addAnchor(point, index ?? this.points.length - 1)
  }

  deletePoint(pointId: string) {
    const points = [...this.points]
    const index = this.points.findIndex((p) => p.id === pointId)

    if (index === -1) {
      return
    }

    points.splice(index, 1)
    this.instance.points(points.map((point) => [point.x, point.y]).reduce((a, b) => [...a].concat(b)))

    this.points = points

    this.#removeAnchor(pointId)

    this.save()
  }

  movePoint(point: IPoint) {
    const points = [...this.points]
    const pointIndex = points.findIndex((p) => p.id === point.id)
    if (pointIndex === -1) {
      return
    }

    points[pointIndex] = point

    this.instance.points(points.map((point) => [point.x, point.y]).reduce((a, b) => [...a].concat(b)))

    this.points = points
  }

  #addAnchor(point: IPoint, index?: number) {
    const anchor = new Anchor({
      shapeId: this.id,
      groupId: this.groupId,
      point,
      movePoint: (p: IPoint) => this.movePoint(p),
    })

    this.anchorGroup.add(anchor.instance)

    if (index) {
      this.anchors.splice(index, 0, anchor)
    } else {
      this.anchors.push(anchor)
    }
  }

  #removeAnchor(pointId: string) {
    const removedIndex = this.anchors.findIndex((t) => t.point.id === pointId)
    const toRemove = this.anchors[removedIndex]
    if (toRemove) {
      toRemove.remove()
      this.anchors.splice(removedIndex, 1)
    }
  }
}
